/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { SwapOutlined } from '@ant-design/icons'
import type { IHeaderParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { useAttendanceSection } from './AttendanceSectionContext'

// AVAILABLE isn't a column sort — it re-groups rows by the current statuses.
export const REGROUP_SORT_KEY = 'available'

export interface AttendanceSortHeaderParams {
  // Sort key handed to useAttendanceSort's handleState.
  sortKey: string
  // Optional explanation shown on hover.
  tooltip?: string
}

/**
 * Header for the attendance grid's sortable columns.
 *
 * The grid's own sorting is off: row order comes from orderAttendanceRows
 * (availability snapshot, promoted-last, then the column sort), which AG Grid
 * comparators can't express. This header drives that sort instead, and borrows
 * AG Grid's own sort-icon classes so it looks like every other DataGrid header.
 */
const AttendanceSortHeader = ({
  displayName,
  sortKey,
  tooltip,
}: IHeaderParams & AttendanceSortHeaderParams) => {
  const { sort } = useAttendanceSection()
  const isRegroup = sortKey === REGROUP_SORT_KEY
  const isActive = !isRegroup && sort.sortType === sortKey

  const ariaSort = isActive
    ? sort.sortOrder === 'asc'
      ? 'ascending'
      : 'descending'
    : undefined

  return (
    <Tooltip title={tooltip} zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}>
      <button
        type='button'
        css={buttonStyle}
        onClick={() => sort.handleState(sortKey)}
        aria-sort={ariaSort}
        aria-description={tooltip}
      >
        <span className='ag-header-cell-text'>{displayName}</span>
        {isRegroup ? (
          <SwapOutlined css={regroupIconStyle} aria-hidden />
        ) : (
          <span
            className={`ag-sort-indicator-icon ${
              isActive
                ? sort.sortOrder === 'asc'
                  ? 'ag-sort-ascending-icon'
                  : 'ag-sort-descending-icon'
                : 'ag-sort-none-icon'
            }`}
            aria-hidden
          >
            <span
              className={`ag-icon ${
                isActive
                  ? sort.sortOrder === 'asc'
                    ? 'ag-icon-asc'
                    : 'ag-icon-desc'
                  : 'ag-icon-none'
              }`}
            />
          </span>
        )}
      </button>
    </Tooltip>
  )
}

export default AttendanceSortHeader

const buttonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.NEUTRAL_50};
    outline-offset: 2px;
    border-radius: 4px;
  }
`
const regroupIconStyle = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_100};
  transform: rotate(90deg);
`
